// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import { SearchableTicker } from "@tmi/types/entities";
import LocalForagePlugin, { tickers } from "@/plugins/LocalForagePlugin";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allTickers.value.length > 0 );
const loading = ref< boolean >( false );

const defaultTickers: SearchableTicker[] = [];
const allTickers = ref< SearchableTicker[] >( defaultTickers );
const searchableEtfs = ref< SearchableTicker[] >( defaultTickers );
const searchableStocks = ref< SearchableTicker[] >( defaultTickers );

export const useTickersStore = defineStore( 'tickers', () => {
    function reset(): void {
        allTickers.value.splice( 0 );
        searchableEtfs.value.splice( 0 );
        searchableStocks.value.splice( 0 );
        loading.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Tickers ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Tickers ] Checking the local database for any tickers...` );

            loading.value = true;

            const t0: number = performance.now();

            if ( typeof tickers === 'undefined' ) {
                // Wait for the driver to settle
                await LocalForagePlugin.initialize();
            }

            await tickers.ready();
            const numberTickers = await tickers.length();

            const uniqueEtfs: Set< SearchableTicker > = new Set< SearchableTicker >();
            const uniqueStocks: Set< SearchableTicker > = new Set< SearchableTicker >();
            if ( numberTickers > 0 ) {
                // Clear the contents of the searchable collections
                allTickers.value.splice( 0 );
                searchableEtfs.value.splice( 0 );
                searchableStocks.value.splice( 0 );

                // Iterate over all elements within IDB
                tickers.iterate( ( ticker: SearchableTicker ) => {
                    ticker.isEtf ? uniqueEtfs.add( ticker ) : uniqueStocks.add( ticker );
                    return;
                } ).then( () => {
                    searchableStocks.value = [ ...uniqueStocks ];
                    searchableEtfs.value = [ ...uniqueEtfs ];
                    allTickers.value = [ ...uniqueStocks, ...uniqueEtfs ];

                    const t1: number = performance.now();
                    logger.info( `(local) Loaded ${ searchableStocks.value.length } stock & ${ searchableEtfs.value.length } etf tickers in ${ t1 - t0 } ms` );
                });
            }
        } catch( err: any ) {
            logger.error( JSON.stringify( err.message ) )
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function tickerById( id: string ): SearchableTicker | undefined {
        return allTickers.value.find( ( ticker: SearchableTicker ): boolean => ticker.id === id );
    }

    return {
        // ACTIONS
        initialize,
        reset,
        tickerById,

        // GETTERS
        allTickers,
        searchableEtfs,
        searchableStocks
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTickersStore, import.meta.hot ) );
}
